"use client";

import posthog from "posthog-js";
import { readBrowserSafeNodeEnv } from "@/lib/browser-safe-env";
import {
  type LogAttributes,
  type LogLevel,
  normalizeConsoleArgs,
  normalizeStructuredLog,
} from "@/lib/logging/shared";
import { isClientTelemetryDeliveryEnabled } from "@/lib/telemetry/environment";

type LoggerMethod = (
  body: string,
  attributes?: Record<string, string | number | boolean>,
) => void;

type PostHogWithLogs = typeof posthog & {
  logger?: Partial<Record<LogLevel, LoggerMethod>>;
  captureLog?: (record: {
    body: string;
    level: LogLevel;
    attributes?: Record<string, string | number | boolean>;
  }) => void;
};

export function createClientLogger(source: string) {
  return {
    trace: (...args: unknown[]) => emitConsoleArgs("trace", source, args),
    debug: (...args: unknown[]) => emitConsoleArgs("debug", source, args),
    info: (...args: unknown[]) => emitConsoleArgs("info", source, args),
    log: (...args: unknown[]) => emitConsoleArgs("info", source, args),
    warn: (...args: unknown[]) => emitConsoleArgs("warn", source, args),
    error: (...args: unknown[]) => emitConsoleArgs("error", source, args),
    fatal: (...args: unknown[]) => emitConsoleArgs("fatal", source, args),
    event: (level: LogLevel, body: string, attributes?: LogAttributes) =>
      emitRecord(normalizeStructuredLog(level, source, body, attributes)),
  };
}

function emitConsoleArgs(level: LogLevel, source: string, args: unknown[]) {
  emitRecord(normalizeConsoleArgs(level, source, args));
}

function emitRecord(record: ReturnType<typeof normalizeStructuredLog>) {
  if (!isClientTelemetryDeliveryEnabled()) return;

  if (record.level === "debug" && readBrowserSafeNodeEnv() === "production") {
    return;
  }

  try {
    const client = posthog as PostHogWithLogs;
    const loggerMethod = client.logger?.[record.level];

    if (loggerMethod) {
      loggerMethod(record.body, record.attributes);
      return;
    }

    client.captureLog?.({
      body: record.body,
      level: record.level,
      attributes: record.attributes,
    });
  } catch {
    // Logging must never break app flows.
  }
}
